<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue';
import { computedAsync } from '@vueuse/core';

import { isExperimentAsync } from '@libs/feature-toggle';

import SharedHeader from '../../core/components/SharedHeader.vue';
import { usePerformanceStore, useStatsigEventsStore } from '../../core/stores';
import { PriceSummaryFooterMobile } from '../../modules/priceSummaryFooterMobile';

import TripSummaryColumn from './components/tripSummary/TripSummaryColumn.vue';
import TripOverviewPageLayoutMobile from './TripOverviewPageLayoutMobile.vue';
import TripOverviewColumn from './components/tripItinerary/TripOverviewColumn.vue';
import { TripOverviewPanelType } from './models';
import {
  useTripOverviewPageStore,
  useTripOverviewPanelsStore,
  useTripSummaryStore,
} from './stores';

const performanceStore = usePerformanceStore();
const tripOverviewPageStore = useTripOverviewPageStore();
const tripOverviewPanelsStore = useTripOverviewPanelsStore();
const tripSummaryStore = useTripSummaryStore();
const statsigEventsStore = useStatsigEventsStore();

const tripSummaryColumnRef = useTemplateRef('tripSummaryColumnRef');

const moblieFooterHeight = '84px';

const isLongScroll = ref<boolean>(false);

const shouldShowSummaryMobileFooter = computed(
  () =>
    tripOverviewPageStore.hasRecipeIngredients &&
    !tripSummaryStore.isTripSummaryVisible &&
    tripOverviewPanelsStore.tripOverviewPanelType ===
      TripOverviewPanelType.None &&
    isLongScroll.value
);

const shouldShowTripSummaryPreCheckout = computedAsync<boolean>(
  () => isExperimentAsync('trip_summary_pre_checkout_experiment'),
  false
);

const shouldUseBookCopy = computedAsync<boolean>(
  () => isExperimentAsync('alterantive_copy_for_mobile_footer'),
  false
);

function onLongScroll(): void {
  isLongScroll.value = true;
}

function scrollToTripSummary(): void {
  statsigEventsStore.logTripOverviewScrollToSummary();

  if (shouldShowTripSummaryPreCheckout.value) {
    tripOverviewPanelsStore.openTripPriceSummaryPreCheckoutPanel();
  } else {
    const el = tripSummaryColumnRef.value?.$el;
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
}
</script>

<template>
  <TripOverviewPageLayoutMobile
    :class="{ 'has-mobile-footer': shouldShowSummaryMobileFooter }"
    @vue:mounted="performanceStore.trackTripOverviewPageRenderedPerformance()"
    @long-scroll="onLongScroll"
  >
    <template #content>
      <SharedHeader :should-show-print-button="false" />
      <TripOverviewColumn />
    </template>
    <template #summary>
      <TripSummaryColumn ref="tripSummaryColumnRef" />
    </template>
  </TripOverviewPageLayoutMobile>

  <PriceSummaryFooterMobile
    v-show="shouldShowSummaryMobileFooter"
    :button-state="tripSummaryStore.bookingButtonState"
    :button-text="
      shouldUseBookCopy ? $t('planner.book') : $t('taxes_and_fees.summary')
    "
    class="trip-summary-mobile-footer"
    @button-clicked="scrollToTripSummary"
  />
</template>

<style lang="scss" scoped>
.has-mobile-footer {
  margin-bottom: v-bind(moblieFooterHeight);
}

.trip-summary-mobile-footer {
  position: fixed;
  bottom: 0;
  left: 0;
  height: v-bind(moblieFooterHeight);
}
</style>
